import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Profile } from '../../types';
import type { TarefaMencionavel } from '../../src/components/comentarios/mencoes';

interface MentionTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    users: Profile[];
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onMention?: (userId: string) => void;
    /** IDs que devem aparecer primeiro na sugestão (ex.: time do departamento da solicitação,
     *  solicitante, responsável) — o resto da empresa continua listado, só depois. */
    priorityUserIds?: string[];
    /** Tarefas sugeridas no gatilho "#". Vazio (padrão) desliga o "#" e nada muda para quem já usa. */
    tarefas?: TarefaMencionavel[];
    onTaskMention?: (tarefaId: string) => void;
}

// Propriedades que precisam ser copiadas pro espelho pra ele quebrar linha exatamente como o
// textarea real — sem isso o "@" calculado cai num lugar diferente de onde a pessoa está digitando.
const MIRROR_PROPERTIES = [
    'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize',
    'lineHeight', 'fontFamily', 'textAlign', 'textTransform', 'textIndent',
    'textDecoration', 'letterSpacing', 'wordSpacing', 'tabSize', 'whiteSpace', 'wordBreak',
] as const;

const MentionTextarea: React.FC<MentionTextareaProps> = ({ users, value, onChange, onMention, className, priorityUserIds, tarefas = [], onTaskMention, ...props }) => {
    const [showMentions, setShowMentions] = useState(false);
    const [gatilho, setGatilho] = useState<'@' | '#'>('@');
    const [mentionQuery, setMentionQuery] = useState('');
    const [cursorPosition, setCursorPosition] = useState(0);
    const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const mirrorRef = useRef<HTMLDivElement>(null);

    // Posição do caractere "@" na tela (não a caixa inteira do textarea) — usa um espelho
    // invisível com o mesmo texto/fonte/largura pra achar onde a linha realmente quebra.
    // Técnica padrão pra isso (textarea não expõe a posição do cursor em pixels sozinho).
    const getCaretCoordinates = (): { top: number; left: number; height: number } | null => {
        const el = textareaRef.current;
        const mirror = mirrorRef.current;
        if (!el || !mirror) return null;

        const style = window.getComputedStyle(el);
        mirror.style.position = 'fixed';
        mirror.style.top = '0';
        mirror.style.left = '0';
        mirror.style.visibility = 'hidden';
        mirror.style.pointerEvents = 'none';
        mirror.style.whiteSpace = 'pre-wrap';
        mirror.style.wordWrap = 'break-word';
        MIRROR_PROPERTIES.forEach(prop => {
            (mirror.style as unknown as Record<string, string>)[prop] = style[prop as keyof CSSStyleDeclaration] as string;
        });

        const pos = el.selectionStart;
        mirror.textContent = value.substring(0, pos);
        const marker = document.createElement('span');
        marker.textContent = value.substring(pos) || '.';
        mirror.appendChild(marker);

        const rect = el.getBoundingClientRect();
        const lineHeight = parseInt(style.lineHeight, 10) || parseInt(style.fontSize, 10) * 1.2 || 20;

        return {
            top: rect.top + marker.offsetTop - el.scrollTop,
            left: rect.left + marker.offsetLeft - el.scrollLeft,
            height: lineHeight,
        };
    };

    // Portal pro <body>, com posição calculada em coordenadas de tela — o popup antes vivia
    // dentro do textarea (position: absolute) e ficava cortado por qualquer ancestral com
    // overflow-hidden, além de ficar grudado na borda da caixa em vez de perto do "@" digitado.
    const updatePopupPosition = () => {
        const caret = getCaretCoordinates();
        if (!caret) return;
        const top = caret.top + caret.height + 4;
        // Sempre abre abaixo da linha do "@" — nada de alternar pra cima, que é o que tava
        // cortando encostado no cabeçalho do campo. Limita a altura ao que sobra de tela pra
        // baixo, pra nunca vazar pra fora da janela mesmo com o "@" perto do rodapé.
        const maxHeight = Math.max(80, Math.min(192, window.innerHeight - top - 12));
        setPopupStyle({ position: 'fixed', left: caret.left, top, width: 256, maxHeight });
    };

    useLayoutEffect(() => {
        if (!showMentions) return;
        updatePopupPosition();
        // capture: true pega scroll de qualquer ancestral com overflow (não só a janela) —
        // o modal tem várias áreas com scroll próprio.
        const onScrollOrResize = () => updatePopupPosition();
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMentions, value]);

    // Handle typing to detect @
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        const pos = e.target.selectionStart;
        onChange(e);

        // Check if we are typing a mention
        const textBeforeCursor = val.substring(0, pos);
        // O gatilho válido é o mais próximo do cursor: digitar "#" depois de um "@" antigo
        // precisa abrir a lista de tarefas, não a de usuários.
        const candidatos: { char: '@' | '#'; pos: number }[] = [
            { char: '@' as const, pos: textBeforeCursor.lastIndexOf('@') },
            ...(tarefas.length > 0 ? [{ char: '#' as const, pos: textBeforeCursor.lastIndexOf('#') }] : []),
        ].filter(c => c.pos !== -1);

        if (candidatos.length > 0) {
            const escolhido = candidatos.reduce((a, b) => (b.pos > a.pos ? b : a));
            const isAtStart = escolhido.pos === 0;
            const isPrecededBySpace = escolhido.pos > 0 && textBeforeCursor[escolhido.pos - 1] === ' ';

            if (isAtStart || isPrecededBySpace) {
                const query = textBeforeCursor.substring(escolhido.pos + 1);
                // Optional: Limit query length or check for newlines
                if (!query.includes('\n') && query.length < 20) {
                    setGatilho(escolhido.char);
                    setMentionQuery(query);
                    setShowMentions(true);
                    setCursorPosition(pos);
                    return;
                }
            }
        }

        setShowMentions(false);
    };

    const insertMention = (user: Profile) => {
        if (!textareaRef.current) return;

        const val = value;
        const pos = textareaRef.current.selectionStart; // This might be after the query
        // We need to find the specific @ we are replacing.
        // Re-calculate lastAt based on current cursor (assuming user hasn't moved it wildly, but safer to re-evaluate)

        const textBeforeCursor = val.substring(0, pos);
        const lastAtPos = textBeforeCursor.lastIndexOf('@');

        const before = val.substring(0, lastAtPos);
        const after = val.substring(pos);
        const newValue = `${before}@${user.full_name} ${after}`;

        // Simulate change event
        const event = {
            target: { value: newValue }
        } as React.ChangeEvent<HTMLTextAreaElement>;

        onChange(event);
        setShowMentions(false);
        textareaRef.current.focus();

        // Trigger callback if needed
        if (onMention) onMention(user.id);
    };

    const insertTaskMention = (tarefa: TarefaMencionavel) => {
        if (!textareaRef.current) return;
        const pos = textareaRef.current.selectionStart;
        const textBeforeCursor = value.substring(0, pos);
        const lastPos = textBeforeCursor.lastIndexOf('#');

        const newValue = `${value.substring(0, lastPos)}#${tarefa.titulo} ${value.substring(pos)}`;
        onChange({ target: { value: newValue } } as React.ChangeEvent<HTMLTextAreaElement>);
        setShowMentions(false);
        textareaRef.current.focus();
        if (onTaskMention) onTaskMention(String(tarefa.id));
    };

    const filteredTarefas = tarefas.filter(t =>
        (t.titulo || '').toLowerCase().includes(mentionQuery.toLowerCase()),
    );

    const priority = new Set(priorityUserIds || []);
    const filteredUsers = users
        .filter(u => u.status !== 'inactive')
        .filter(u =>
            (u.full_name || '').toLowerCase().includes(mentionQuery.toLowerCase()) ||
            (u.email || '').toLowerCase().includes(mentionQuery.toLowerCase())
        )
        // Quem é do contexto da solicitação (departamento, solicitante, responsável) sobe pro
        // topo — sem isso a sugestão listava a empresa inteira sem prioridade nenhuma.
        .sort((a, b) => Number(priority.has(b.id)) - Number(priority.has(a.id)));

    return (
        <div className="relative w-full">
            <textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                className={`w-full ${className}`}
                {...props}
            />

            {/* Espelho invisível só pra medir onde o "@" cai na tela — nunca aparece. */}
            <div ref={mirrorRef} aria-hidden="true" />

            {showMentions && gatilho === '@' && filteredUsers.length > 0 && createPortal(
                <div style={popupStyle} className="z-[200] bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto">
                    {filteredUsers.map(user => (
                        <button
                            key={user.id}
                            type="button"
                            onClick={() => insertMention(user)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs text-primary font-bold">
                                {(user.full_name || user.email || '?').substring(0, 2).toUpperCase()}
                            </div>
                            <div className="flex flex-col">
                                <span className="font-medium">{user.full_name || user.email}</span>
                                <span className="text-[10px] text-gray-400">{user.email}</span>
                            </div>
                        </button>
                    ))}
                </div>,
                document.body
            )}

            {showMentions && gatilho === '#' && filteredTarefas.length > 0 && createPortal(
                <div style={popupStyle} className="z-[200] bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto">
                    {filteredTarefas.map(tarefa => (
                        <button
                            key={tarefa.id}
                            type="button"
                            onClick={() => insertTaskMention(tarefa)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <span className="font-medium">{tarefa.titulo}</span>
                            <span className="block text-[10px] text-gray-400">Tarefa #{tarefa.id}</span>
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

export default MentionTextarea;
